/**
 * Portfolio Card Component
 *
 * 포트폴리오 카드 컴포넌트 - 개별 포트폴리오 정보 표시
 * - 리더 정보 (프로필, 팔로워 수)
 * - 수익률 통계 (ROI 30일/90일)
 * - Sharpe Ratio, Max Drawdown
 * - Copy 버튼
 */

import React, { useContext, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { ThemeContext } from '@/theme/ThemeProvider';
import { useToken } from '@/app/hooks';

import type {
  CopyPortfolio,
  CopyPortfolioStats,
} from '../../../../types/copyTrading';

import AvatarIcon from '@/assets/icons/avatar.svg?react';
import TwoUserIcon from '@/assets/icons/2-User.svg?react';
import GrowIcon from '@/assets/icons/up-grow.svg?react';
import D30Icon from '@/assets/icons/30d.svg?react';
import D90Icon from '@/assets/icons/90d.svg?react';
import MountingIcon from '@/assets/icons/mountine.svg?react';

import { formatDecimal } from '@utils/formatDecimal';
import { useTranslation } from 'react-i18next';

interface PortfolioCardProps {
  portfolio: CopyPortfolio;
  stats?: CopyPortfolioStats;
  onCopy?: (portfolio: CopyPortfolio) => void;
  isFollowing?: boolean; // 이미 팔로우 중인지 여부
  isMyPortfolio?: boolean; // 자기 자신의 포트폴리오 여부
}

export const PortfolioCard: React.FC<PortfolioCardProps> = React.memo(
  ({ portfolio, stats, onCopy, isFollowing = false, isMyPortfolio = false }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isDarkMode } = useContext(ThemeContext);
    const { userid } = useToken(); // ⭐ 현재 로그인한 사용자 ID

    const handleCardClick = useCallback(() => {
      navigate(`/copy-trade/portfolio/${portfolio.id}`);
    }, [navigate, portfolio.id]);

    // ⭐ 자기 자신의 포트폴리오 체크 (handleCopyClick 전에 선언)
    const isOwnPortfolio = useMemo(() => {
      return userid !== null && portfolio.leaderUserId === userid;
    }, [userid, portfolio.leaderUserId]);

    const handleCopyClick = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();

        // 이미 팔로우 중이거나 자기 자신의 포트폴리오면 클릭 방지
        if (isFollowing || isOwnPortfolio || isMyPortfolio) {
          return;
        }

        if (onCopy) {
          onCopy(portfolio);
        }
      },
      [onCopy, portfolio, isFollowing, isOwnPortfolio, isMyPortfolio]
    );

    // Status 체크 (메모이제이션)
    const { isActive, isPaused } = useMemo(
      () => ({
        isActive: portfolio.status === 1,
        isPaused: portfolio.status === 2,
      }),
      [portfolio.status]
    );

    // 버튼 텍스트 결정
    const buttonText = useMemo(() => {
      if (isOwnPortfolio) return 'My Portfolio'; // ⭐ 자기 포트폴리오
      if (isFollowing) return 'Following';
      if (isPaused) return 'Paused';
      if (isActive) return 'Copy';
      return 'Closed';
    }, [isOwnPortfolio, isFollowing, isPaused, isActive]);

    // 프로필 이미지 URL 생성
    const profileImageUrl = useMemo(() => {
      if (!portfolio.imageUrl) return null;

      // 미디어 베이스 URL 사용
      const mediaBaseUrl = import.meta.env.VITE_APP_API_MEDIA_DEV_URL;

      return `${mediaBaseUrl}/${portfolio.imageUrl}`;
    }, [portfolio.imageUrl]);

    return (
      <CardContainer onClick={handleCardClick} isDark={isDarkMode}>
        {/* Section 1: 리더 정보 */}
        <Section1>
          <Avatar $hasImage={!!profileImageUrl}>
            {profileImageUrl ? (
              <img src={profileImageUrl} alt={portfolio.portfolioName} />
            ) : (
              <AvatarIcon />
            )}
          </Avatar>
          <ContentWrap>
            <PortfolioTitle>{portfolio.portfolioName}</PortfolioTitle>
            <LeaderName>by {portfolio.leaderUsername}</LeaderName>
            <InfoRow>
              <FollowerInfo>
                <TwoUserIcon />
                <span>
                  {portfolio.currentFollowers || 0} {t('followers')}
                </span>
              </FollowerInfo>
              <PnlBadge positive={portfolio.totalPnl >= 0}>
                <GrowIcon />
                <span>
                  {portfolio.totalPnl >= 0 ? '+' : ''}
                  {formatDecimal(portfolio.totalPnl, 2)} USDT
                </span>
              </PnlBadge>
            </InfoRow>
          </ContentWrap>
        </Section1>

        {/* Section 2: 포트폴리오 정보 (실제 데이터) */}
        <Section2>
          <InfoItem>
            <InfoLabel>{t('profitShareRate')}</InfoLabel>
            <InfoValue>
              {portfolio.profitShareRate
                ? formatDecimal(portfolio.profitShareRate, 1)
                : '0'}
              %
            </InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>{t('minCopyAmount')}</InfoLabel>
            <InfoValue>
              {portfolio.minCopyAmount
                ? formatDecimal(portfolio.minCopyAmount, 0)
                : '100'}{' '}
              USDT
            </InfoValue>
          </InfoItem>
        </Section2>

        {/* Copy Button */}
        <CopyButton
          onClick={handleCopyClick}
          disabled={!isActive || isFollowing || isOwnPortfolio || isMyPortfolio}
          isDark={isDarkMode}
          isFollowing={isFollowing}
          isOwnPortfolio={isOwnPortfolio}
        >
          {buttonText}
        </CopyButton>

        {/* Status Badge */}
        {!isActive && (
          <StatusBadge status={portfolio.status}>
            {isPaused ? 'Paused' : 'Closed'}
          </StatusBadge>
        )}
      </CardContainer>
    );
  }
);

// ==========================================
// Styled Components
// ==========================================

const CardContainer = styled.div<{ isDark: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  border-radius: 12px;
  background-color: ${({ isDark }) => (isDark ? '#1a1a1a' : '#ffffff')};
  border: ${({ isDark }) => (isDark ? '1px solid #333' : '1px solid #e0e0e0')};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  width: 100%;

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ isDark }) =>
      isDark
        ? '0 4px 12px rgba(0, 0, 0, 0.5)'
        : '0 4px 12px rgba(0, 0, 0, 0.1)'};
  }
`;

const Section1 = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Avatar = styled.div<{ $hasImage?: boolean }>`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  ${({ $hasImage }) =>
    $hasImage &&
    `
    background-color: transparent;
  `}

  svg {
    width: 32px;
    height: 32px;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ContentWrap = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const PortfolioTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.text};
  margin-bottom: 2px;
`;

const LeaderName = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #888;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FollowerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  color: ${({ theme }) => theme.text};

  svg {
    width: 16px;
    height: 16px;
  }
`;

const PnlBadge = styled.div<{ positive: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 600;
  color: ${({ positive }) => (positive ? '#22c55e' : '#ef4444')};

  svg {
    width: 14px;
    height: 14px;
  }
`;

const Section2 = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ROIMain = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ROILabel = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  color: ${({ theme }) => theme.text};

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ROIValue = styled.div<{ positive?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 20px;
  font-weight: 700;
  color: ${({ positive }) =>
    positive === undefined ? '#888' : positive ? '#22c55e' : '#ef4444'};

  svg {
    width: 18px;
    height: 18px;
  }
`;

const Section3 = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StatRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StatLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: ${({ theme }) => theme.text};
  opacity: 0.7;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const StatValue = styled.div<{ positive?: boolean }>`
  font-size: 14px;
  font-weight: 600;
  color: ${({ positive }) =>
    positive === undefined ? '#888' : positive ? '#22c55e' : '#ef4444'};
`;

const CopyButton = styled.button<{
  disabled: boolean;
  isDark: boolean;
  isFollowing?: boolean;
  isOwnPortfolio?: boolean;
}>`
  width: 100%;
  height: 40px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  background-color: ${({ disabled, isDark, isFollowing, isOwnPortfolio }) => {
    if (isOwnPortfolio) return isDark ? '#2d3748' : '#e2e8f0'; // ⭐ 자기 포트폴리오
    if (isFollowing) return isDark ? '#1f2937' : '#f3f4f6';
    if (disabled) return isDark ? '#333' : '#e0e0e0';
    return '#3b82f6';
  }};
  color: ${({ disabled, isDark, isFollowing, isOwnPortfolio }) => {
    if (isOwnPortfolio) return isDark ? '#a0aec0' : '#718096'; // ⭐ 자기 포트폴리오
    if (isFollowing) return isDark ? '#9ca3af' : '#6b7280';
    if (disabled) return '#888';
    return '#fff';
  }};
  border: ${({ isFollowing, isOwnPortfolio, isDark }) => {
    if (isOwnPortfolio)
      return isDark ? '1px solid #4a5568' : '1px solid #cbd5e0'; // ⭐ 자기 포트폴리오
    if (isFollowing) return isDark ? '1px solid #374151' : '1px solid #d1d5db';
    return 'none';
  }};
  cursor: ${({ disabled, isFollowing, isOwnPortfolio }) =>
    disabled || isFollowing || isOwnPortfolio ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ disabled, isDark, isFollowing, isOwnPortfolio }) => {
      if (disabled || isFollowing || isOwnPortfolio)
        return isDark ? '#333' : '#e0e0e0';
      return '#2563eb';
    }};
  }
`;

const StatusBadge = styled.div<{ status: number }>`
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background-color: ${({ status }) => (status === 2 ? '#f59e0b' : '#ef4444')};
  color: #fff;
`;

const InfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const InfoLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #888;
`;

const InfoValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
`;
